import React, { useState, useEffect } from 'react';
import { Grade, Subject, Board, LearningOutcomeItem, Tenant, UserProfile } from '../types';
import { Dropdown } from './Dropdown';
import { FileUpload } from './FileUpload';
import { GRADES, SUBJECTS, BOARDS } from '../constants';
import { runService } from '../services/runService';
import { extractTextFromPdf } from '../utils/pdfUtils';
import { generateLessonPlanPdf, LessonPlanInputs } from '../utils/pdfGenerator';
import { useAuth } from '../contexts/AuthContext';

interface ShapeShifterModuleProps {
  tenant: Tenant;
  userProfile: UserProfile;
  brandLogo: string;
  partnerLogo: string;
}

export const ShapeShifterModule: React.FC<ShapeShifterModuleProps> = ({ tenant, userProfile, brandLogo, partnerLogo }) => {
  const [showInputForm, setShowInputForm] = useState<boolean>(false);
  const [board, setBoard] = useState<Board>(Board.CBSE);
  const [grade, setGrade] = useState<Grade>(Grade.GRADE_8);
  const [subject, setSubject] = useState<Subject>(Subject.SOCIAL_STUDIES);
  const [chapterFile, setChapterFile] = useState<File | null>(null);
  const [numPeriods, setNumPeriods] = useState<string>('1');
  const [periodDuration, setPeriodDuration] = useState<string>('40');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedPlanText, setGeneratedPlanText] = useState<string | null>(null);
  const [lessonInputs, setLessonInputs] = useState<LessonPlanInputs | null>(null);
  const [isGenerationComplete, setIsGenerationComplete] = useState<boolean>(false);
  const [tokenUsage, setTokenUsage] = useState<{ input: number; output: number; model: string; } | null>(null);

  const { updateTenant } = useAuth();

  

  const handleGenerateLessonPlan = async () => {
    if (!chapterFile) {
      setError("Please upload the textbook chapter PDF.");
      return;
    }
    if (parseInt(numPeriods) <= 0 || parseInt(periodDuration) <= 0) {
      setError("Number and duration of periods must be positive numbers.");
      return;
    }

    setError(null);
    setLoading(true);
    setGeneratedPlanText(null);
    setIsGenerationComplete(false);
    setTokenUsage(null);
    setLessonInputs(null);

    try {

      
    const result = await runService.startRun(tenant, userProfile, 'shapeshifter', {
  board: String(board).trim(),
  grade,
  subject,
  chapterFile,
  learningOutcomesText: '',
  numPeriods,
  periodDuration,
});


      const { resultText, promptTokens, completionTokens, model } = result.aiResult;
      
      const inputsForPdf = { grade, subject, chapterName: chapterFile.name };
      setLessonInputs(inputsForPdf);
      setGeneratedPlanText(resultText);
      setTokenUsage({ input: promptTokens, output: completionTokens, model });
      
      updateTenant(result.newTenant);

     generateLessonPlanPdf(resultText, inputsForPdf, brandLogo, partnerLogo, tenant?.tenantLogoUrl);
      setIsGenerationComplete(true);

    } catch (err: any) {
      console.error("Lesson plan generation failed:", err);
      setError(err.message || "Failed to generate lesson plan. Please ensure the PDF is accessible and valid.");
    } finally {
      setLoading(false);
    }
  };

  const resetFormState = () => {
    setBoard(Board.CBSE);
    setGrade(Grade.GRADE_8);
    setSubject(Subject.SOCIAL_STUDIES);
    setChapterFile(null);
    setNumPeriods('1');
    setPeriodDuration('40');
    setLoading(false);
    setError(null);
    setGeneratedPlanText(null);
    setIsGenerationComplete(false);
    setTokenUsage(null);
    setLessonInputs(null);
  };

 

  return (
    <div className="bg-white shadow-xl rounded-xl border border-gray-200 p-8 md:p-10 lg:p-12 flex flex-col h-full transition-all duration-300 ease-in-out hover:shadow-2xl hover:-translate-y-2">
      <h2 className="text-3xl font-extrabold text-brand-grey mb-8 border-b-2 border-brand-orange pb-2">
        <span className="text-brand-orange">Shape Shifter</span><sup className="text-sm">™</sup>: Lesson <span className="text-brand-orange">Redesign</span>
      </h2>
      <div className="flex-grow flex flex-col">
        {!showInputForm ? (
          <div
            onClick={() => {
              resetFormState();
              setShowInputForm(true);
            }}
            className="flex-grow flex flex-col justify-center items-center text-center cursor-pointer hover:bg-gray-50 transition-colors duration-200 p-4 rounded-b-xl"
            role="button"
            aria-label="Open Lesson Plan Architect Module"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-brand-orange mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-brand-grey text-lg mb-6 max-w-xs">
              Reworks board-prescribed chapters into classroom-ready lessons without changing outcomes or increasing workload.
            </p>
            <p className="text-brand-orange font-semibold mt-auto pt-4">
              Design a lesson plan →
            </p>
          </div>
        ) : (
          <>
            <button
              onClick={() => {
                resetFormState();
                setShowInputForm(false);
              }}
              className="mb-6 px-4 py-2 bg-gray-200 text-brand-grey text-sm font-semibold rounded-md shadow-sm hover:bg-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50"
              aria-label="Back to module selection"
            >
              ← Back
            </button>

            <Dropdown
              label="Board"
              options={BOARDS}
              selectedValue={board}
              onValueChange={(value) => setBoard(value as Board)}
              id="shapeshifter-board-select"
            />

            <Dropdown
              label="Grade"
              options={GRADES}
              selectedValue={grade}
              onValueChange={(value) => setGrade(value as Grade)}
              id="shapeshifter-grade-select"
            />

            <Dropdown
              label="Subject"
              options={SUBJECTS}
              selectedValue={subject}
              onValueChange={(value) => setSubject(value as Subject)}
              id="shapeshifter-subject-select"
            />

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="num-periods" className="block text-sm font-bold text-brand-grey mb-1">
                  Number of Periods
                </label>
                <input
                  type="number"
                  id="num-periods"
                  value={numPeriods}
                  onChange={(e) => setNumPeriods(e.target.value)}
                  min="1"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-orange focus:border-brand-orange sm:text-sm text-brand-grey bg-white"
                />
              </div>
              <div>
                <label htmlFor="period-duration" className="block text-sm font-bold text-brand-grey mb-1">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  id="period-duration"
                  value={periodDuration}
                  onChange={(e) => setPeriodDuration(e.target.value)}
                  min="1"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-orange focus:border-brand-orange sm:text-sm text-brand-grey bg-white"
                />
              </div>
            </div>

            <FileUpload
              label="Textbook Chapter (PDF)"
              accept=".pdf"
              onFileChange={(files) => setChapterFile(files[0] || null)}
            />
            
          

            {error && !isGenerationComplete && <p className="mb-4 text-sm text-red-600">{error}</p>}

            <button
  onClick={handleGenerateLessonPlan}
  disabled={loading || !chapterFile}
  className={`w-full mt-4 px-6 py-3 bg-brand-orange text-white text-lg font-semibold rounded-md shadow-md hover:bg-opacity-90 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-opacity-50 ${
    (loading || !chapterFile) ? 'opacity-50 cursor-not-allowed' : ''
  }`}
  aria-label="Generate Lesson Plan"
>

              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 text-white inline-block mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Processing & Generating...</span>
                </div>
              ) : (
                'Generate Lesson Plan PDF'
              )}
            </button>

            {isGenerationComplete && generatedPlanText && lessonInputs && (
              <div className="mt-8 text-center bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg">
                <p className="font-semibold">Success! Your lesson plan PDF has been generated.</p>
                <p className="text-sm">Your download should have started automatically.</p>
         
      
                <button
onClick={() => generateLessonPlanPdf(generatedPlanText, lessonInputs, brandLogo, partnerLogo, tenant?.tenantLogoUrl)}
                  className="mt-4 px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-md shadow-sm hover:bg-opacity-90 transition-colors duration-200"
                >
                  Download PDF Again
                </button>
              </div>
            )}
             {error && isGenerationComplete && (
               <p className="mt-4 text-sm text-red-600">{error}</p>
             )}
          </>
        )}
      </div>
    </div>
  );
};